import "crypto-js/lib-typedarrays";

import { zodResolver } from "@hookform/resolvers/zod";
import { CredentialResponse, GoogleLogin, useGoogleOneTapLogin } from "@react-oauth/google";
import { AuthenticationDetails, CognitoUser } from "amazon-cognito-identity-js";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { Controller, useForm } from "react-hook-form";
import { decodeToken } from "react-jwt";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";
import { queryClient } from "../../../../App";

import ErrorToast from "../../../components/ErrorToast";
import PasswordInput from "../../../components/PasswordInput";
import Spinner from "../../../components/Spinner";
import { Button } from "../../../components/ui/button";
import { Checkbox } from "../../../components/ui/checkbox";
import { Field, FieldError, FieldLabel } from "../../../components/ui/field";
import { Input } from "../../../components/ui/input";
import { useAuth } from "../../../context/Auth/AuthContext";
import { StaticValue } from "../../../seed/constants";
import { applyAuthorizationHeader } from "../../../services/apiService";
import { hasUserRecord } from "../../../services/auth/accountRecovery";
import { userPool } from "../../../services/auth/cognito";
import { redirectAfterSignIn } from "../../../services/auth/postAuthRedirect";
import ResendVerificationButton from "../components/ResendVerificationButton";
import {
	clearPendingSignup,
	clearPendingVerificationEmail,
	getPendingSignup,
	SignupMethod,
	setPendingSignup,
} from "../pendingSignup";
import LinkedInLogin from "./LinkedInLogin";

const staticValue = new StaticValue();
const staticValues = staticValue.getDefaultValues();

const EASE = [0.22, 1, 0.36, 1] as [number, number, number, number];

interface IdTokenPayload {
	email: string;
	given_name: string;
	family_name: string;
	picture: string;
}

const signinSchema = z.object({
	email: z.string().min(1, "Email is required").email("Enter a valid email address"),
	password: z.string().min(1, "Password is required"),
	remember_me: z.boolean(),
});

type SigninFormValues = z.infer<typeof signinSchema>;

/**
 * Sign-in succeeded, but the identity may have no backend user document (see accountRecovery).
 * Those users have to finish onboarding first — sending them to their profile instead means every
 * authenticated call 401s and the response interceptor bounces them straight back to this form with
 * no explanation of what went wrong.
 */
async function goToDestination(
	profile: IdTokenPayload,
	method: SignupMethod,
	token: string,
	redirectOnWeb: (destination: string) => void,
) {
	const userHasRecord = await hasUserRecord(token);

	// The anonymous or previous-account result must not survive this sign-in when navigation stays
	// inside the SPA.
	queryClient.removeQueries({ queryKey: ["user", "account"] });

	if (!userHasRecord) {
		// Keep any answers this account already gave. Someone who filled in onboarding and was then
		// sent here (an expired token mid-submit, say) would otherwise have to answer everything
		// again, because the rebuilt draft below only carries what the identity provider knows.
		const existingDraft = getPendingSignup();
		const answersForThisAccount =
			existingDraft?.email?.trim().toLowerCase() === profile.email?.trim().toLowerCase() ? existingDraft : undefined;

		// Cognito only ever stores the email attribute, so given_name/family_name are undefined for
		// email accounts. Onboarding asks for the name when the draft doesn't carry one.
		setPendingSignup({
			...answersForThisAccount,
			firstName: answersForThisAccount?.firstName || profile.given_name || "",
			lastName: answersForThisAccount?.lastName || profile.family_name || "",
			email: profile.email,
			method,
			emailVerified: true,
		});
		redirectOnWeb("/onboarding");
		return;
	}

	// Nothing left to finish — drop any abandoned draft so /creating-profile can't replay it.
	clearPendingSignup();
	redirectAfterSignIn(token, redirectOnWeb);
}

function SigninForm() {
	const {
		control,
		formState: { errors },
		handleSubmit: submitForm,
		register,
		reset,
	} = useForm<SigninFormValues>({
		resolver: zodResolver(signinSchema),
		defaultValues: {
			email: "",
			password: "",
			remember_me: false,
		},
	});

	const { setUserData } = useAuth();
	const navigate = useNavigate();
	const redirectWithinApp = (destination: string) => navigate(destination, { replace: true });

	useEffect(() => {
		const storedRememberMe = localStorage.getItem("remember_me");
		if (!storedRememberMe) {
			return;
		}

		const rememberedValues = JSON.parse(storedRememberMe) as Pick<SigninFormValues, "email" | "remember_me">;

		reset({
			email: rememberedValues.email,
			password: "",
			remember_me: rememberedValues.remember_me,
		});
	}, [reset]);

	const [_, setCookie, removeCookie] = useCookies<string>(["Authorization"]);
	const [error, setError] = useState({ serverErrors: "" });
	// Set when Cognito reports the account exists but the email link was never followed.
	const [unconfirmedEmail, setUnconfirmedEmail] = useState("");
	const [loading, setLoading] = useState(false);

	function handleSubmit({ email, password, remember_me }: SigninFormValues) {
		setError({ serverErrors: "" });
		setUnconfirmedEmail("");
		setLoading(true);

		if (remember_me) {
			localStorage.setItem("remember_me", JSON.stringify({ email, remember_me }));
		} else {
			localStorage.removeItem("remember_me");
		}

		let authenticationData = {
			Username: email,
			Password: password,
		};

		let authenticationDetails = new AuthenticationDetails(authenticationData);

		let userData = {
			Username: email,
			Pool: userPool,
		};

		let cognitoUser = new CognitoUser(userData);

		cognitoUser.authenticateUser(authenticationDetails, {
			onSuccess: function (result) {
				let idToken = `CO::${result.getIdToken().getJwtToken()}`;
				const expireDate = new Date();
				expireDate.setDate(expireDate.getDate() + 30);
				setCookie("Authorization", idToken, {
					path: "/",
					expires: expireDate,
				});
				applyAuthorizationHeader(idToken);

				// They made it in — stop showing the "check your email" screen at /creating-profile.
				clearPendingVerificationEmail();

				let cognitoUser = userPool.getCurrentUser();

				if (cognitoUser != null) {
					cognitoUser.getSession(function (err, result) {
						if (result) {
							// `path: "/"` matters: the pointer is written at the root, and a
							// removal without it targets /signin instead, so the cookie survives
							// and this account opens the previous visitor's resume.
							removeCookie(staticValues.localStorageResumeId, { path: "/" });
							localStorage.removeItem("resumeList");
							queryClient.removeQueries({ queryKey: ["resumes"] });
							setLoading(false);
							const decodedToken = decodeToken<IdTokenPayload>(result.getIdToken().getJwtToken());

							const data = {
								email: decodedToken.email,
								lastName: decodedToken.family_name,
								firstName: decodedToken.given_name,
								picture: decodedToken.picture,
							};

							localStorage.setItem("profileData", JSON.stringify(data));
							setUserData(data);

							void goToDestination(decodedToken, "email", idToken, redirectWithinApp);
						}
					});
				}
			},
			onFailure: function (err) {
				const code = err?.code || err?.name;

				if (code === "UserNotConfirmedException") {
					// The account exists, the password was right — they just never followed the emailed
					// activation link. Setting this renders the resend button below the message.
					setUnconfirmedEmail(email);
					setError((prev) => ({
						...prev,
						serverErrors:
							"You haven't verified your account yet. Follow the activation link we emailed you to finish signing in.",
					}));
				} else if (code === "NotAuthorizedException") {
					setError((prev) => ({ ...prev, serverErrors: "Incorrect email or password" }));
				} else if (code === "UserNotFoundException") {
					setError((prev) => ({ ...prev, serverErrors: "We couldn't find an account with that email address." }));
				} else if (code === "PasswordResetRequiredException") {
					setError((prev) => ({
						...prev,
						serverErrors: "Your password needs to be reset — use “I forgot password” below.",
					}));
				} else {
					// Don't blame verification for errors we haven't identified — that used to send verified
					// users off to hunt for an activation email that had nothing to do with the failure.
					setError((prev) => ({
						...prev,
						serverErrors: err?.message || "We couldn't sign you in. Please try again.",
					}));
				}
				setLoading(false);
			},
		});
	}

	function handleInvalidSubmit() {
		setError({ serverErrors: "" });
		setUnconfirmedEmail("");
	}

	function onGoogleSignIn(googleUser: CredentialResponse) {
		if (googleUser.credential) {
			const credential = `GO::${googleUser.credential}`;

			const expireDate = new Date();
			expireDate.setDate(expireDate.getDate() + 30);
			setCookie("Authorization", credential, {
				path: "/",
				expires: expireDate,
			});
			applyAuthorizationHeader(credential);

			// Same as the email path: the pointer lives at `path: "/"` and a removal
			// without it silently leaves the previous visitor's resume selected.
			removeCookie(staticValues.localStorageResumeId, { path: "/" });
			localStorage.removeItem("resumeList");
			queryClient.removeQueries({ queryKey: ["resumes"] });
			setLoading(false);
			const decodedToken = decodeToken<IdTokenPayload>(googleUser.credential);

			const data = {
				...decodedToken,
				email: decodedToken.email,
				firstName: decodedToken.given_name,
				lastName: decodedToken.family_name,
				picture: decodedToken.picture,
			};

			localStorage.setItem("profileData", JSON.stringify(data));
			setUserData(data);

			void goToDestination(decodedToken, "google", credential, redirectWithinApp);
		}
	}

	const [showEmailOptions, setShowEmailOptions] = useState(false);
	const reduceMotion = useReducedMotion();

	const profileData = localStorage.getItem("profileData");

	useGoogleOneTapLogin({
		onSuccess: onGoogleSignIn,
		auto_select: !!profileData,
	});

	const buttonRef = useRef<HTMLDivElement>(null);

	const buttonWidth = useMemo(() => {
		if (buttonRef.current) {
			return buttonRef.current.offsetWidth;
		}
		return 0;
	}, [buttonRef.current]);

	return (
		<>
			<div className="flex flex-col gap-3">
				<LinkedInLogin />
				<div className="flex items-center justify-center" ref={buttonRef}>
					<GoogleLogin
						onSuccess={onGoogleSignIn}
						onError={() => toast.error("Login Failed")}
						size="large"
						width={`${buttonWidth}px`}
						text="continue_with"
					/>
				</div>
			</div>
			<div className="my-5 flex items-center gap-3" aria-hidden="true">
				<span className="h-px flex-1 bg-gray-200" />
				<span className="text-xs font-medium uppercase tracking-wide text-gray-400">or</span>
				<span className="h-px flex-1 bg-gray-200" />
			</div>
			<div className="">{error.serverErrors !== "" && <ErrorToast error={error.serverErrors} />}</div>
			{unconfirmedEmail && (
				<ResendVerificationButton email={unconfirmedEmail} source="signin" className="mb-4 text-center" />
			)}
			<AnimatePresence initial={false} mode="wait">
				{showEmailOptions ? (
					<motion.div
						key="email-form"
						initial={reduceMotion ? false : { height: 0, opacity: 0 }}
						animate={{ height: "auto", opacity: 1 }}
						exit={reduceMotion ? { opacity: 0 } : { height: 0, opacity: 0 }}
						transition={reduceMotion ? { duration: 0 } : { duration: 0.35, ease: EASE }}
						className="overflow-hidden"
					>
						<form noValidate onSubmit={submitForm(handleSubmit, handleInvalidSubmit)} className="tr-form">
							<Field className="gap-1 mb-4" data-invalid={!!errors.email}>
								<FieldLabel htmlFor="signin-email">Email</FieldLabel>
								<Input
									id="signin-email"
									type="email"
									placeholder="Enter your email address"
									aria-invalid={!!errors.email}
									aria-describedby={errors.email ? "signin-email-error" : undefined}
									{...register("email")}
								/>
								<FieldError id="signin-email-error" className="text-error-default" errors={[errors.email]} />
							</Field>
							<Field className="gap-1 mb-4" data-invalid={!!errors.password}>
								<FieldLabel htmlFor="signin-password">Password</FieldLabel>
								<PasswordInput
									id="signin-password"
									placeholder="Enter your password"
									aria-invalid={!!errors.password}
									aria-describedby={errors.password ? "signin-password-error" : undefined}
									{...register("password")}
								/>
								<FieldError id="signin-password-error" className="text-error-default" errors={[errors.password]} />
							</Field>
							<div className="flex justify-between mb-4">
								<Controller
									name="remember_me"
									control={control}
									render={({ field }) => (
										<Field orientation="horizontal" className="w-auto gap-0">
											<Checkbox
												id="signin-remember-me"
												name={field.name}
												checked={field.value}
												onCheckedChange={(checked) => field.onChange(checked === true)}
												onBlur={field.onBlur}
											/>
											<FieldLabel htmlFor="signin-remember-me" className="pl-2 text-sm font-medium m-0 text-gray-800">
												Remember me
											</FieldLabel>
										</Field>
									)}
								/>
								<div className="forgot-password">
									<Link to="/forgot-password" className="text-sm font-medium text-primary-700">
										I forgot my password
									</Link>
								</div>
							</div>

							<Button type="submit" className="w-full" disabled={loading}>
								{loading ? <Spinner /> : "Log in"}
							</Button>
						</form>
					</motion.div>
				) : (
					<motion.div
						key="email-button"
						initial={reduceMotion ? false : { opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={reduceMotion ? { duration: 0 } : { duration: 0.12, ease: EASE }}
					>
						<Button
							type="button"
							variant="outlineSecondary"
							className="w-full"
							onClick={() => setShowEmailOptions(true)}
						>
							Continue with Email
						</Button>
					</motion.div>
				)}
			</AnimatePresence>
			<div className="mt-6 text-sm text-gray-600">
				<span>
					New to Dproz?{" "}
					<Link to={"/register?ref=" + window.location.pathname} className="font-semibold text-primary-700">
						Create a free account
					</Link>
				</span>
			</div>
		</>
	);
}

export default SigninForm;
